<template>
  <aside v-if="items.length" class="breaking" :class="{'breaking--expanded': expanded}">
    <header class="breaking__header">
      <span class="breaking__dot"></span>
      <h3 class="breaking__title">Lo último</h3>
      <span v-if="expanded" class="breaking__count">{{ total }}</span>
      <button
        class="breaking__toggle"
        :title="expanded ? 'Ver menos' : 'Ver todas'"
        :aria-label="expanded ? 'Ver menos' : 'Ver todas'"
        @click="toggle"
      >
        <!-- Minimizar / Maximizar -->
        <svg v-if="expanded" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
          <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
        </svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
          <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
        </svg>
      </button>
    </header>

    <!-- Marquesina (abajo → arriba); se pausa con hover -->
    <div
      v-if="!expanded"
      class="breaking__viewport"
      :class="{'breaking__viewport--paused': paused}"
      @touchstart.passive="pause"
      @touchend="resumeLater"
    >
      <ul class="breaking__track" :style="{animationDuration: items.length * 4 + 's'}">
        <li v-for="(it, i) in marqueeItems" :key="i" class="breaking__item">
          <a :href="it.link" target="_blank" rel="noopener noreferrer">
            <span class="breaking__time">{{ it.time }}</span>
            <span class="breaking__headline">{{ it.headline }}</span>
            <span v-if="it.source" class="breaking__source">{{ it.source }}</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Listado completo -->
    <ul v-else class="breaking__list">
      <li v-for="it in items" :key="it.id" class="breaking__item">
        <a :href="it.link" target="_blank" rel="noopener noreferrer">
          <span class="breaking__time">{{ it.time }}</span>
          <span class="breaking__headline">{{ it.headline }}</span>
          <span v-if="it.source" class="breaking__source">{{ it.source }}</span>
        </a>
      </li>
      <li v-if="loadingAll" class="breaking__loading">Cargando...</li>
    </ul>
  </aside>
</template>

<script>
import feedChunksConfig from '../../../config/feedChunks.config';

const PREFIX = 'googlenews';

export default {
  name: 'BreakingNewsCard',
  data() {
    return {
      items: [],
      total: 0,
      totalChunks: 0,
      loadedChunks: 0,
      expanded: false,
      loadingAll: false,
      paused: false
    };
  },
  computed: {
    // Lista duplicada para que la animación (translateY -50%) sea continua
    marqueeItems() {
      return [...this.items, ...this.items];
    }
  },
  methods: {
    // En táctil no hay hover: tocar pausa la marquesina y se reanuda 3s después de soltar
    pause() {
      clearTimeout(this.resumeTimer);
      this.paused = true;
    },
    resumeLater() {
      this.resumeTimer = setTimeout(() => { this.paused = false; }, 3000);
    },
    async fetchJson(file) {
      const res = await fetch(`${feedChunksConfig.baseUrl}${file}`);
      if (!res.ok) throw new Error(`No se pudo cargar ${file}`);
      return res.json();
    },
    async loadChunk() {
      const raw = await this.fetchJson(`${PREFIX}-all-chunk-${this.loadedChunks}.json`);
      this.loadedChunks++;
      this.items.push(...raw.map(item => {
        // Google News agrega " - Fuente" al final del título
        const title = item.title || '';
        const cut = title.lastIndexOf(' - ');
        const date = item.pubDate ? new Date(item.pubDate) : null;
        return {
          id: item.id,
          link: item.link,
          headline: cut > 0 ? title.slice(0, cut) : title,
          source: cut > 0 ? title.slice(cut + 3) : '',
          time: date ? date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : ''
        };
      }));
    },
    async toggle() {
      this.expanded = !this.expanded;
      if (!this.expanded || this.loadingAll) return;
      this.loadingAll = true;
      try {
        while (this.loadedChunks < this.totalChunks) await this.loadChunk();
      } catch (e) {
        console.error('Error cargando noticias de última hora:', e);
      } finally {
        this.loadingAll = false;
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.resumeTimer);
  },
  async mounted() {
    try {
      const meta = await this.fetchJson(`${PREFIX}-metadata.json`);
      this.total = meta.byType?.[PREFIX]?.totalItems || 0;
      this.totalChunks = meta.byType?.[PREFIX]?.totalChunks || 0;
      if (this.totalChunks) await this.loadChunk();
    } catch (e) {
      console.error('Error cargando noticias de última hora:', e);
    }
  }
};
</script>

<style scoped>
/* Fija en la última columna, ocupando 2 filas del grid */
.breaking {
  grid-column: -2 / -1;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  min-height: 420px;
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #1c2127 40%);
  border: 1px solid rgba(239, 68, 68, 0.45);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.breaking--expanded {
  grid-column: 1 / -1;
  grid-row: 1;
}

.breaking__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(239, 68, 68, 0.25);
}

.breaking__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: breaking-pulse 1.4s ease-in-out infinite;
}

.breaking__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fca5a5;
}

.breaking__count {
  font-size: 12px;
  color: #7d8694;
}

.breaking__viewport {
  flex: 1;
  position: relative;
  overflow: hidden;
  mask-image: linear-gradient(transparent, #000 12%, #000 88%, transparent);
  -webkit-mask-image: linear-gradient(transparent, #000 12%, #000 88%, transparent);
}

.breaking__track {
  position: absolute;
  inset: 0 0 auto 0;
  margin: 0;
  padding: 0;
  list-style: none;
  animation: breaking-scroll linear infinite;
  animation-delay: 3s; /* pausa inicial para leer la primera noticia */
}

.breaking__viewport:hover .breaking__track,
.breaking__viewport--paused .breaking__track {
  animation-play-state: paused;
}

.breaking__list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 70vh;
  overflow-y: auto;
}

.breaking__item a {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-bottom: 1px solid #2d333b;
  color: #e1e7ef;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.breaking__item a:hover {
  background: rgba(239, 68, 68, 0.08);
}

.breaking__item a:hover .breaking__headline {
  color: #fca5a5;
}

.breaking__time {
  font-size: 11px;
  font-weight: 700;
  color: #ef4444;
}

.breaking__headline {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.breaking__source {
  font-size: 12px;
  color: #7d8694;
}

.breaking__loading {
  padding: 12px 16px;
  font-size: 13px;
  color: #7d8694;
}

.breaking__toggle {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 6px;
  color: #fca5a5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.breaking__toggle:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
}

@keyframes breaking-scroll {
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
}

@keyframes breaking-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

@media (prefers-reduced-motion: reduce) {
  .breaking__track { animation: none; }
  .breaking__viewport { overflow-y: auto; }
}

@media (max-width: 768px) {
  .breaking {
    grid-row: 1;
    min-height: 360px;
  }
}
</style>
